@if (isOpen) {
  <div class="panel-overlay" [class.open]="isOpen" (click)="onOverlayClick($event)">
    <div class="create-panel" [class.open]="isOpen" (click)="$event.stopPropagation()">
      <!-- Panel Header -->
      <div class="panel-header">
        <h2>{{ isEditMode ? 'Edit Project' : 'Create Project' }}</h2>
        <button class="close-btn" (click)="onClose()" aria-label="Close panel">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <line x1="18" y1="6" x2="6" y2="18"/>
            <line x1="6" y1="6" x2="18" y2="18"/>
          </svg>
        </button>
      </div>

      <!-- Panel Content -->
      <div class="panel-content">
        <form [formGroup]="createProjectForm" (ngSubmit)="onSubmit()">
          <!-- Project Name -->
          <div class="form-group">
            <label for="projectName">Project name <span class="required">*</span></label>
            <input
              type="text"
              id="projectName"
              formControlName="name"
              placeholder="e.g., Test Project"
              autocomplete="off"
            />
            @if (createProjectForm.get('name')?.touched && createProjectForm.get('name')?.invalid) {
              <span class="error-message">
                @if (createProjectForm.get('name')?.errors?.['required']) {
                  Project name is required
                } @else if (createProjectForm.get('name')?.errors?.['minlength']) {
                  Name must be at least 2 characters
                }
              </span>
            }
          </div>

          <!-- Description -->
          <div class="form-group">
            <label for="description">Description</label>
            <textarea
              id="description"
              formControlName="description"
              placeholder="Brief description of the project..."
              rows="3"
            ></textarea>
          </div>

          <!-- Project Color -->
          <div class="form-group">
            <label>Project color</label>
            <div class="color-picker">
              @for (color of colorPalette; track color.value) {
                <button
                  type="button"
                  class="color-swatch"
                  [class.selected]="selectedColor === color.value"
                  [style.background-color]="color.value"
                  [attr.aria-label]="'Select ' + color.name"
                  (click)="selectColor(color.value)"
                >
                  @if (selectedColor === color.value) {
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="3">
                      <polyline points="20 6 9 17 4 12"/>
                    </svg>
                  }
                </button>
              }
            </div>
          </div>

          <!-- Project Icon -->
          <div class="form-group">
            <label>Project icon</label>
            <div
              class="icon-upload-zone"
              [class.dragging]="isDraggingFile"
              [class.has-preview]="iconPreview"
              (dragover)="onDragOver($event)"
              (dragleave)="onDragLeave($event)"
              (drop)="onDrop($event)"
            >
              @if (iconPreview) {
                <div class="icon-preview">
                  <img [src]="iconPreview" alt="Project icon preview" />
                  <button type="button" class="remove-icon" (click)="removeIcon()" aria-label="Remove icon">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                      <line x1="18" y1="6" x2="6" y2="18"/>
                      <line x1="6" y1="6" x2="18" y2="18"/>
                    </svg>
                  </button>
                </div>
              } @else {
                <div class="upload-content">
                  <div class="initials-preview" [style.background-color]="selectedColor">
                    <span>{{ projectInitials || '?' }}</span>
                  </div>
                  <div class="upload-text">
                    <span class="upload-label">Drop image or <label for="iconUpload" class="upload-link">click to upload</label></span>
                    <span class="upload-hint">PNG, JPG, SVG up to 2MB</span>
                  </div>
                </div>
                <input
                  type="file"
                  id="iconUpload"
                  accept="image/*"
                  (change)="onFileSelect($event)"
                  hidden
                />
              }
            </div>
          </div>

          <!-- Sites Section (Collapsible) -->
          <div class="sites-section">
            <button type="button" class="sites-toggle" (click)="toggleSitesSection()">
              <span class="toggle-icon" [class.expanded]="sitesExpanded">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <polyline points="6 9 12 15 18 9"/>
                </svg>
              </span>
              <span>{{ isEditMode ? 'Manage sites' : 'Add sites (optional)' }}</span>
            </button>

            @if (sitesExpanded) {
              <div class="sites-list" formArrayName="sites">
                @for (siteControl of sites.controls; track i; let i = $index) {
                  <div class="site-row" [formGroupName]="i">
                    <input
                      type="text"
                      formControlName="domain"
                      placeholder="www.example.com"
                      autocomplete="off"
                    />
                    <button type="button" class="remove-site-btn" (click)="removeSite(i)" aria-label="Remove site">
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <line x1="18" y1="6" x2="6" y2="18"/>
                        <line x1="6" y1="6" x2="18" y2="18"/>
                      </svg>
                    </button>
                  </div>
                }
                <button type="button" class="add-site-btn" (click)="addSite()">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <line x1="12" y1="5" x2="12" y2="19"/>
                    <line x1="5" y1="12" x2="19" y2="12"/>
                  </svg>
                  Add another site
                </button>
              </div>
            }
          </div>
        </form>
      </div>

      <!-- Panel Footer -->
      <div class="panel-footer">
        <button type="button" class="btn-secondary" (click)="onClose()">Cancel</button>
        @if (showSaveButton) {
          <button
            type="submit"
            class="btn-primary"
            [disabled]="createProjectForm.invalid"
            (click)="onSubmit()"
          >
            {{ isEditMode ? 'Save Changes' : 'Create Project' }}
          </button>
        }
      </div>
    </div>
  </div>
}
